import { capitalize } from 'lodash';

export const incrementalMetricDef = (metricLabel: string) =>
  `Incremental ${metricLabel} is the modelled effect of the selected media. This can be either in currency or units depending on the type of KPI data used in the model.`;

export const MEDIA_INVESTMENT_DEF =
  'Media Investment is calculated by summing the spend or cost used for each selected media.';

export const observedMetricDef = (metricLabel: string) =>
  `${metricLabel} Data is the actual ${metricLabel.toLowerCase()} data used in the model.`;

export const predictedMetricDef = (metricLabel: string) =>
  `Predicted ${metricLabel} is the ${metricLabel.toLowerCase()} the model predicts.`;

export const euroRoiDef = (metricLabel: string) => `ROI is calculated by:
(Incremental ${metricLabel} - Media Investment) / Media Investment`;

export const unitRoiDef = (metricLabel: string) => `ROI is calculated by:
Incremental ${metricLabel} / Media Investment`;

export const romiDef = (metricLabel: string) => `ROMI is calculated by:
Total spend = Media Investment + Production Costs
Modelled Margin = Incremental ${metricLabel} * Margin Percentage
ROMI = (Modelled Margin - Total Spend) / Total Spend`;

export const totalRoiDef = (metricLabel: string) =>
  `Total ROI is calculated by first summing all Incremental ${metricLabel} and Media Investments before calculating the ROI.`;

export const totalRomiDef = (metricLabel: string) =>
  `Total ROMI is calculated by first summing all Incremental ${metricLabel}, Media Investments, and Production Costs before calculating the ROMI.`;

export const mediaEffectDef = (
  metricLabel: string,
) => `Media effect percentage is calculated by:
(100 * Incremental ${metricLabel} for the media) / Total Predicted ${metricLabel}`;

export const getCPUDef = (label: string, metricLabel: string) =>
  `${capitalizeLabel(label)} is calculated by:
Media Investment / Incremental ${metricLabel} for the media`;

export const getTotalCPUDef = (label: string, metricLabel: string) => {
  const capitalisedLabel = capitalizeLabel(label);
  return `Total ${capitalisedLabel} is calculated by first summing all Incremental ${metricLabel} and Media Investment before calculating ${capitalisedLabel}.`;
};

export const totalDecompsDef = (metricLabel: string) =>
  `Total Decompositions group data into Base, Media Effect and other factors in the model that explain ${metricLabel.toLowerCase()}.

By using the Base selector you are able to effect what factors (other than Media Effect) are part of the Base.`;

export const responseCurvesDef = (metricLabel: string) =>
  `Response Curves model how a change in the media spending would effect ${metricLabel.toLowerCase()}.

The highlighted point on the curve is the current average spend used for the media.`;

export const SPEND_DEF =
  'Spend is calculated by summing the media spend for each selected channel.';

export const cpaDef = (
  metricLabel: string,
) => `CPA (Cost per Acquisition) is calculated by:
Media Spend / Modelled Media-Driven ${metricLabel}`;

export const totalMetricDef = (metricLabel: string) =>
  `Total ${metricLabel} is the total observed ${metricLabel.toLowerCase()} for the selected time range.`;

export const modelledMetricDef = (metricLabel: string) =>
  `Modelled Media-Driven ${metricLabel} is the share of ${metricLabel.toLowerCase()} the model attributes to the selected media.`;

export const TOTAL_EFFECT_DEF =
  'Total effect is the combined short term and long term media effect on the KPI.';

export const SHORT_TERM_EFFECT_DEF =
  'Short term effect is the immediate media effect on the KPI within the purchase cycle.';

export const LONG_TERM_EFFECT_DEF =
  'Long term effect is the delayed media effect on the KPI, driven by carryover and brand building.';

const capitalizeLabel = (label: string) => {
  return label
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
};
